import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { Observable, filter, Subscription, take } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ProfileService } from './profile.service';
import { AuthService, Contractor } from '@umss/shared/data-access';
import { ToastService } from '@umss/shared/ui';

/**
 * Stateful service that manages contractor profile data using Angular signals.
 * Provides a single source of truth for profile data across the application.
 *
 * Features:
 * - Single API call for profile data with caching
 * - Automatic reactivity with Angular signals
 * - `whenReady()` observable for consumers that need to wait for profile
 * - Auto-clears cached data on logout (auth state change)
 * - Cancels in-flight requests on clear/refresh to prevent stale data
 */
@Injectable({ providedIn: 'root' })
export class ProfileStateService {
  private readonly profileService = inject(ProfileService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);

  // Subscription tracker to cancel in-flight requests
  private loadSubscription?: Subscription;

  // Private signals for internal state management
  private readonly profileSignal = signal<Contractor | null>(null);
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<Error | null>(null);

  // Public readonly signals - components can read but not modify
  readonly profile = this.profileSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  // Observable of profile signal - created in injection context for toObservable() compatibility
  private readonly profile$ = toObservable(this.profileSignal);

  // Computed signals for commonly used profile properties
  readonly scopeOfPractice = computed(() => this.profile()?.scopeOfPractice);

  readonly scopeOfPracticeDisplay = computed(
    () => this.profile()?.scopeOfPracticeDisplay
  );

  readonly contractorId = computed(() => this.profile()?.id);

  readonly contractorName = computed(() => {
    const profile = this.profile();
    if (!profile?.user) return undefined;
    return (
      profile.user.fullName ||
      (profile.user.firstName && profile.user.lastName
        ? `${profile.user.firstName} ${profile.user.lastName}`
        : undefined)
    );
  });

  constructor() {
    // Auto-clear profile when auth state changes (logout or user switch)
    effect(() => {
      const authState = this.authService.authState();
      if (!authState.lastSessionAt) {
        this.clearProfile();
      }
    });
  }

  /**
   * Returns an observable that emits the Contractor profile once it's loaded.
   * If the profile is already loaded, emits immediately.
   * If not yet loaded, triggers a load and waits for the result.
   *
   * Usage:
   * ```ts
   * this.profileState.whenReady().subscribe(profile => {
   *   // profile is guaranteed to be non-null
   * });
   * ```
   */
  whenReady(): Observable<Contractor> {
    // Trigger load if not already loaded or loading
    this.loadProfile();

    return this.profile$.pipe(
      filter((profile): profile is Contractor => profile !== null),
      take(1)
    );
  }

  /**
   * Load the contractor profile from the API.
   * If profile is already loaded or currently loading, this is a no-op.
   */
  loadProfile(): void {
    // Skip if already loaded or currently loading
    if (this.profileSignal() || this.loadingSignal()) {
      return;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    // Cancel any previous in-flight request
    this.loadSubscription?.unsubscribe();

    this.loadSubscription = this.profileService.getProfile().subscribe({
      next: (profile) => {
        this.profileSignal.set(profile);
        this.loadingSignal.set(false);
      },
      error: (error) => {
        this.toastService.openToast({
          type: 'Error',
          title:
            error.error?.message ||
            'Failed to load contractor profile. Please try again.'
        });
        this.errorSignal.set(error);
        this.loadingSignal.set(false);
      }
    });
  }

  /**
   * Force refresh the profile data from the API.
   * Clears cached data and makes a new request.
   */
  refreshProfile(): void {
    this.loadSubscription?.unsubscribe();
    this.profileSignal.set(null);
    this.loadingSignal.set(false);
    this.loadProfile();
  }

  /**
   * Update the cached profile data without making an API call.
   * Useful after profile updates to keep UI in sync.
   */
  updateProfile(profile: Contractor): void {
    this.profileSignal.set(profile);
  }

  /**
   * Clear all profile state and cancel any in-flight requests.
   * Called automatically on logout via auth state listener.
   */
  clearProfile(): void {
    this.loadSubscription?.unsubscribe();
    this.profileSignal.set(null);
    this.loadingSignal.set(false);
    this.errorSignal.set(null);
  }
}
